/*
 * Pseudo Product custom element.
 *
 * Drives the host "pseudo product" page: the shopper picks an injected product
 * and its variant(s) from dropdowns, and this element swaps the gallery, title,
 * price and description, then writes the resolved injected variant ID into the
 * add-to-cart form's hidden [name=id]. The stock product-form.js handles the
 * actual /cart/add submission unchanged.
 *
 * See sections/main-product-pseudo-branded.liquid and PLAN.md.
 */
if (!customElements.get('pseudo-product')) {
  customElements.define(
    'pseudo-product',
    class PseudoProduct extends HTMLElement {
      connectedCallback() {
        this.products = this.parseData();
        if (!this.products.length) return;

        // Collection-sourced families arrive in the collection's own sort order;
        // present them alphabetically. Metaobject-curated families keep the
        // merchandiser's chosen order (flag off).
        if (this.dataset.sortAlpha === 'true') {
          this.products.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        }

        this.productSelect = this.querySelector('[data-pseudo-product-select]');
        this.optionsContainer = this.querySelector('[data-pseudo-options]');
        // Variant option controls render as dropdowns or pills, per the
        // selector block setting emitted onto the options container.
        this.variantStyle = this.optionsContainer?.dataset.variantStyle || 'dropdown';
        // When enabled, unavailable (sold out) option values are not rendered.
        this.hideSoldOut = this.optionsContainer?.dataset.hideSoldOut === 'true';
        // Chooser-side companion to the family filter: when ON (the default),
        // products not purchasable at the filtered value, whether not offered
        // ("Unavailable") or out of stock ("Sold out"), are removed from the
        // product chooser instead of being listed annotated.
        this.chooserHideFiltered = this.optionsContainer?.dataset.hideFilterUnavailable === 'true';
        // Family-wide option filter slots (option_filter blocks). Each slot
        // pins ONE option, matched by name, outside the chooser block: it
        // renders the union of that option's values across every product in
        // the family and holds the shopper's choice as they switch products.
        this.optionSlots = Array.from(this.querySelectorAll('[data-pseudo-option-slot]')).filter(
          (slot) => (slot.dataset.optionName || '').trim() !== ''
        );
        // Sticky option choices keyed by normalized option name, fed by every
        // control change and honored whenever a product is (re)built.
        this.chosenByName = {};
        this.sectionId = this.dataset.sectionId || '';
        // Caret markup to clone into each generated option <select> so they
        // match the server-rendered product select.
        this.caretTemplate = this.productSelect?.closest('.select')?.querySelector('.svg-wrapper');
        this.titleEl = this.querySelector('[data-pseudo-title]');
        this.descriptionEl = this.querySelector('[data-pseudo-description]');
        this.priceEl = this.querySelector('[data-pseudo-price]');
        this.compareEl = this.querySelector('[data-pseudo-compare]');
        // Price block can be set to show the total for the selected quantity.
        this.priceSyncQuantity = this.querySelector('[data-pseudo-price-wrapper]')?.dataset.syncQuantity === 'true';
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.quantityInput = this.querySelector('.quantity__input');
        this.mainImage = this.querySelector('[data-pseudo-main-image]');
        this.thumbsEl = this.querySelector('[data-pseudo-thumbs]');
        this.variantIdInput = this.querySelector('[data-pseudo-variant-id]');
        this.submitButton = this.querySelector('[data-pseudo-submit]');
        this.submitButtonText = this.querySelector('[data-pseudo-submit-text]');
        this.learnMore = this.querySelector('[data-pseudo-learn-more]');

        // Subscription (native Shopify selling plans) block, optional.
        this.subscriptionWrapper = this.querySelector('[data-pseudo-subscription-wrapper]');
        this.sellingPlanInput = this.querySelector('[data-pseudo-selling-plan]');
        this.onetimePriceEl = this.querySelector('[data-pseudo-onetime-price]');
        this.subPriceEl = this.querySelector('[data-pseudo-sub-price]');
        this.subCompareEl = this.querySelector('[data-pseudo-sub-compare]');
        this.subBadgeEl = this.querySelector('[data-pseudo-sub-badge]');
        this.frequencyWrap = this.querySelector('[data-pseudo-frequency-wrap]');
        this.frequencySelect = this.querySelector('[data-pseudo-frequency]');
        // Preserve the chosen delivery cadence across product/variant switches by
        // name (plan ids differ per product), defaulting to the first plan.
        this.selectedFrequencyName = null;

        this.strings = window.variantStrings || {};

        this.productSelect?.addEventListener('change', () => {
          this.selectProduct(parseInt(this.productSelect.value, 10));
        });

        // Keep the (total) price in sync as the quantity changes.
        if (this.quantityInput && this.priceSyncQuantity) {
          ['input', 'change'].forEach((evt) =>
            this.quantityInput.addEventListener(evt, () => this.updatePrice(this.currentVariant))
          );
        }

        // Subscription: purchase-option (one-time/subscribe) and frequency changes.
        if (this.subscriptionWrapper) {
          this.querySelectorAll('[data-pseudo-purchase-option]').forEach((radio) =>
            radio.addEventListener('change', () => this.onPurchaseOptionChange())
          );
          this.frequencySelect?.addEventListener('change', () => this.onFrequencyChange());
        }

        // Family switcher (heading): navigate to the chosen sibling family.
        this.familySwitch = this.querySelector('[data-pseudo-family-switch]');
        this.familySwitch?.addEventListener('change', () => {
          const url = this.familySwitch.value;
          if (url) window.location.href = url;
        });

        // Build the product chooser from the (full) payload so large,
        // collection-sourced families show every product, not just the first 50
        // the server can render into the <select>. Handles the dropdown,
        // searchable, and grouped chooser blocks (whichever is present).
        this.setupChooser();

        this.selectProduct(0);
      }

      // ----- Product chooser (dropdown / searchable / grouped) -----

      // Populate the product <select> with options for the given product indices
      // (defaults to all). Option value is the index into this.products, so the
      // options can be a filtered subset while selection stays index-stable.
      // The pill chooser, when present, mirrors the same index list, so the
      // search filter shrinks both without any extra plumbing.
      buildChooserOptions(indices) {
        const list = indices || this.products.map((_, i) => i);
        const selections = this.filterSelections();
        if (this.productSelect) {
          this.productSelect.innerHTML = '';
          list.forEach((i) => {
            const product = this.products[i];
            if (!product) return;
            const option = document.createElement('option');
            option.value = String(i);
            const ann = this.chooserAnnotation(product, selections);
            option.textContent = ann.text ? `${product.title} (${ann.text})` : product.title;
            this.productSelect.appendChild(option);
          });
        }
        this.buildChooserPills(list, selections);
      }

      // Pill chooser (selector_search block, chooser_style = pill): one radio +
      // label per product, reusing Dawn's .product-form__input--pill CSS. The
      // hidden <select> stays authoritative, so a pill click just mirrors into it
      // before selecting.
      buildChooserPills(list, selections) {
        if (!this.chooserPills) return;
        if (!selections) selections = this.filterSelections();
        this.chooserPills.innerHTML = '';
        list.forEach((i) => {
          const product = this.products[i];
          if (!product) return;
          const id = `PseudoProductPill-${this.sectionId}-${i}`;

          const input = document.createElement('input');
          input.type = 'radio';
          input.id = id;
          input.name = `pseudo-product-${this.sectionId}`;
          input.value = String(i);
          input.checked = i === this.currentIndex;
          input.addEventListener('change', () => {
            if (this.productSelect) this.productSelect.value = String(i);
            this.selectProduct(i);
          });

          const label = document.createElement('label');
          label.setAttribute('for', id);
          label.textContent = product.title;

          // Under a family filter every pill carries the product's price at
          // the filtered value (or its unavailability), so shoppers can
          // compare siblings without visiting them. Without a filter the pill
          // stays a plain title, as before.
          if (selections.length) {
            const ann = this.chooserAnnotation(product, selections);
            const price = document.createElement('span');
            price.className = 'pseudo-chooser-pill__price';
            price.textContent = ann.text;
            price.hidden = !ann.text;
            label.appendChild(price);
            label.classList.toggle('is-unavailable', ann.unavailable);
          }

          this.chooserPills.appendChild(input);
          this.chooserPills.appendChild(label);
        });
      }

      // Keep the pill chooser's checked state in step with selections made
      // elsewhere (first paint, and the search jumping to the first match).
      syncChooserPills() {
        if (!this.chooserPills) return;
        this.chooserPills.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.checked = radio.value === String(this.currentIndex);
        });
      }

      // Wire whichever chooser block is present. All three drive selection through
      // the shared [data-pseudo-product-select] change listener set up above.
      setupChooser() {
        this.searchInput = this.querySelector('[data-pseudo-search]');
        this.searchEmpty = this.querySelector('[data-pseudo-search-empty]');
        this.groupSelect = this.querySelector('[data-pseudo-group]');
        this.chooserPills = this.querySelector('[data-pseudo-product-pills]');

        if (this.groupSelect) {
          this.buildGroups();
          this.groupSelect.value = this.productCategory(this.products[0]);
          this.buildChooserOptions(this.indicesInCategory(this.groupSelect.value));
          this.groupSelect.addEventListener('change', () => this.onGroupChange());
        } else if (this.searchInput) {
          this.buildChooserOptions();
          this.searchInput.addEventListener('input', () => this.onSearchInput());
        } else {
          this.buildChooserOptions();
        }

        // Reflect the initial product (index 0) in the select if it is visible.
        if (this.productSelect && Array.from(this.productSelect.options).some((o) => o.value === '0')) {
          this.productSelect.value = '0';
        }
      }

      // The chooser's base index list from the active chooser UI (search query
      // or category), before the family-filter visibility pass.
      currentBaseIndices() {
        if (this.groupSelect) return this.indicesInCategory(this.groupSelect.value);
        if (this.searchInput) {
          const query = this.searchInput.value.trim().toLowerCase();
          return this.products
            .map((_, i) => i)
            .filter((i) => !query || (this.products[i].title || '').toLowerCase().includes(query));
        }
        return this.products.map((_, i) => i);
      }

      // Drop products that are not purchasable at the current family-filter
      // selection: both the "(Unavailable)" entries (value not offered) and
      // the "(Sold out)" ones (offered, no stock). Every listed entry is
      // therefore buyable at the filtered value. No-op when the block setting
      // is off or no filter is active.
      applyFilterVisibility(indices) {
        if (!this.chooserHideFiltered) return indices;
        const selections = this.filterSelections();
        if (!selections.length) return indices;
        return indices.filter((i) => {
          const product = this.products[i];
          if (!product) return false;
          const variant = this.variantForFilter(product, selections);
          return !!(variant && variant.available);
        });
      }

      // Rebuild the chooser list under the current query/category plus filter
      // visibility. When the current product just got filtered out, advance to
      // the first visible product, mirroring the search box's jump behavior.
      rebuildChooserVisibility() {
        const visible = this.applyFilterVisibility(this.currentBaseIndices());
        this.buildChooserOptions(visible);
        if (this.searchEmpty) this.searchEmpty.hidden = visible.length > 0;
        if (visible.includes(this.currentIndex)) {
          if (this.productSelect) this.productSelect.value = String(this.currentIndex);
        } else if (visible.length) {
          if (this.productSelect) this.productSelect.value = String(visible[0]);
          this.selectProduct(visible[0]);
        }
      }

      // Chooser upkeep at the end of every resolve. A CHANGED filter selection
      // re-lists the chooser (membership may change); anything else only
      // re-annotates the existing entries in place.
      updateChooserForResolve() {
        const selections = this.filterSelections();
        const key = JSON.stringify(selections);
        const changed = key !== this.lastChooserFilterKey;
        this.lastChooserFilterKey = key;
        if (this.chooserHideFiltered && selections.length && changed) {
          this.rebuildChooserVisibility();
        } else {
          this.refreshChooserAnnotations();
        }
      }

      // Searchable chooser: filter the <select> options by the query (plus the
      // filter visibility pass). Keep the current product if still visible,
      // else jump to the first match.
      onSearchInput() {
        const visible = this.applyFilterVisibility(this.currentBaseIndices());
        this.buildChooserOptions(visible);
        if (this.searchEmpty) this.searchEmpty.hidden = visible.length > 0;
        if (visible.includes(this.currentIndex)) {
          this.productSelect.value = String(this.currentIndex);
        } else if (visible.length) {
          this.productSelect.value = String(visible[0]);
          this.selectProduct(visible[0]);
        }
      }

      // Grouped chooser: category -> products within it.
      productCategory(product) {
        const title = (product && product.title ? product.title : '').toLowerCase();
        if (/sugar[\s-]?free/.test(title)) return 'Sugar-Free';
        // Word boundaries so "pump" does not match "pumpkin".
        if (/\bpump\b|\bpourer\b|pour spout|\baccessor/.test(title)) return 'Accessories';
        return 'Regular';
      }

      indicesInCategory(category) {
        return this.products.map((_, i) => i).filter((i) => this.productCategory(this.products[i]) === category);
      }

      buildGroups() {
        const order = ['Regular', 'Sugar-Free', 'Seasonal', 'Accessories'];
        const cats = [];
        this.products.forEach((p) => {
          const c = this.productCategory(p);
          if (!cats.includes(c)) cats.push(c);
        });
        cats.sort((a, b) => {
          const ia = order.indexOf(a);
          const ib = order.indexOf(b);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
        });
        this.groupSelect.innerHTML = '';
        cats.forEach((c) => {
          const option = document.createElement('option');
          option.value = c;
          option.textContent = c;
          this.groupSelect.appendChild(option);
        });
      }

      onGroupChange() {
        const visible = this.applyFilterVisibility(this.indicesInCategory(this.groupSelect.value));
        this.buildChooserOptions(visible);
        if (visible.length) {
          this.productSelect.value = String(visible[0]);
          this.selectProduct(visible[0]);
        }
      }

      parseData() {
        const script = this.querySelector('[data-pseudo-data]');
        if (!script) return [];
        try {
          const parsed = JSON.parse(script.textContent);
          // Drop any product that has no purchasable variant data.
          return Array.isArray(parsed) ? parsed.filter((p) => p && Array.isArray(p.variants) && p.variants.length) : [];
        } catch (e) {
          console.error('[pseudo-product] Could not parse product data', e);
          return [];
        }
      }

      get currentProduct() {
        return this.products[this.currentIndex] || null;
      }

      selectProduct(index) {
        if (!this.products[index]) index = 0;
        this.currentIndex = index;
        const product = this.currentProduct;
        if (!product) return;

        this.syncChooserPills();

        if (this.titleEl) this.titleEl.textContent = product.title;

        // Point the "learn more" CTA at the selected product's canonical listing.
        if (this.learnMore && product.url) this.learnMore.href = product.url;

        if (this.descriptionEl) {
          // Descriptions live in <template data-pseudo-desc="i"> to keep RTE
          // HTML (which may contain </script>) out of the JSON payload.
          const tpl = this.querySelector(`[data-pseudo-desc="${index}"]`);
          this.descriptionEl.innerHTML = tpl ? tpl.innerHTML : '';
        }

        this.buildOptionSelects(product);
        this.buildGallery(product);
        this.resolveVariant();
      }

      // Ordered, unique values for each option index, derived from the variants.
      optionValues(product, optionIndex) {
        const seen = [];
        product.variants.forEach((v) => {
          const value = v.options[optionIndex];
          if (value != null && !seen.includes(value)) seen.push(value);
        });
        return seen;
      }

      // Is any variant carrying this option value purchasable? Used to drop
      // sold-out values when hideSoldOut is on. For the common single-option
      // (1:1 value-to-variant) pseudo product this is exact; for multi-option
      // products a value is hidden only when every variant with it is sold out.
      isValueAvailable(product, optionIndex, value) {
        return product.variants.some((v) => v.options[optionIndex] === value && v.available);
      }

      // ----- Family-wide option filters (option_filter block slots) -----

      // Normalized option-name key used for matching and the sticky store.
      normName(name) {
        return String(name || '')
          .trim()
          .toLowerCase();
      }

      optionIndexByName(product, name) {
        const want = this.normName(name);
        return (product && product.options ? product.options : []).findIndex((n) => this.normName(n) === want);
      }

      slotForOption(optionName) {
        const want = this.normName(optionName);
        return this.optionSlots.find((slot) => this.normName(slot.dataset.optionName) === want) || null;
      }

      // Union of an option's values across EVERY product in the family, in
      // count order (see totalCountForValue), unparsable values last in
      // first-seen order. With hideSoldOut, a value is dropped only when no
      // product in the family has stock for it.
      unionOptionValues(optionName, hideSoldOut) {
        const seen = [];
        const anyAvailable = {};
        this.products.forEach((product) => {
          const idx = this.optionIndexByName(product, optionName);
          if (idx < 0) return;
          product.variants.forEach((v) => {
            const value = v.options[idx];
            if (value == null) return;
            if (!seen.includes(value)) seen.push(value);
            if (v.available) anyAvailable[value] = true;
          });
        });
        let values = seen;
        if (hideSoldOut) values = values.filter((value) => anyAvailable[value]);
        const rank = (value) => {
          const total = this.totalCountForValue(value);
          const packs = (this.parseCountValue(value) || { packs: 0 }).packs;
          return [total == null ? Number.MAX_SAFE_INTEGER : total, packs];
        };
        return values
          .map((value, i) => ({ value, i }))
          .sort((a, b) => {
            const ra = rank(a.value);
            const rb = rank(b.value);
            return ra[0] - rb[0] || ra[1] - rb[1] || a.i - b.i;
          })
          .map((x) => x.value);
      }

      // Cheapest variant ANY product in the family offers at this value,
      // available ones first. Backs the "from $X" price on a filter card whose
      // value the current product does not offer.
      cheapestFamilyVariant(optionName, value) {
        let best = null;
        const better = (v) => {
          if (!best) return true;
          if (v.available !== best.available) return v.available;
          if (v.price_amount == null || best.price_amount == null) return false;
          return v.price_amount < best.price_amount;
        };
        this.products.forEach((product) => {
          const idx = this.optionIndexByName(product, optionName);
          if (idx < 0) return;
          product.variants.forEach((v) => {
            if (v.options[idx] === value && better(v)) best = v;
          });
        });
        return best;
      }

      // Can ANY product in the family sell this value right now?
      familyValueAvailable(optionName, value) {
        return this.products.some((product) => {
          const idx = this.optionIndexByName(product, optionName);
          return idx >= 0 && product.variants.some((v) => v.options[idx] === value && v.available);
        });
      }

      // The values currently selected in the filter slots: [{name, value}]
      // with names normalized. Empty when no filter block is present, which
      // keeps every pre-filter page on its old behavior.
      filterSelections() {
        const selections = [];
        (this.optionSlots || []).forEach((slot) => {
          const field = slot.querySelector('[data-pseudo-option-field]');
          if (!field) return;
          const select = field.querySelector('select');
          const checked = field.querySelector('input[type="radio"]:checked');
          const value = select ? select.value : checked ? checked.value : null;
          if (value != null) selections.push({ name: this.normName(field.dataset.optionName), value });
        });
        return selections;
      }

      get hasFilters() {
        return this.filterSelections().length > 0;
      }

      // A product's variant under the family filter, availability-preferred.
      // Null when the product lacks the option name or the value: that product
      // is unavailable in this Count.
      variantForFilter(product, selections) {
        let matched = product.variants;
        for (const s of selections) {
          const idx = this.optionIndexByName(product, s.name);
          if (idx < 0) return null;
          matched = matched.filter((v) => v.options[idx] === s.value);
          if (!matched.length) return null;
        }
        return matched.find((v) => v.available) || matched[0] || null;
      }

      // Chooser entry annotation: the product's price at the filtered value,
      // or why it has none. Without a filter, the first available variant's
      // price (the pre-filter behavior).
      chooserAnnotation(product, selections) {
        if (!selections.length) {
          const variant = product.variants.find((v) => v.available) || product.variants[0];
          return { text: variant && variant.price ? variant.price : '', unavailable: false };
        }
        const variant = this.variantForFilter(product, selections);
        if (!variant) return { text: this.strings.unavailable || 'Unavailable', unavailable: true };
        if (!variant.available) return { text: this.strings.soldOut || 'Sold out', unavailable: true };
        return { text: variant.price || '', unavailable: false };
      }

      // Re-annotate every chooser entry in place (no rebuild, so an open
      // dropdown and keyboard focus survive a filter change).
      refreshChooserAnnotations() {
        const selections = this.filterSelections();
        if (!selections.length) return;
        if (this.productSelect) {
          Array.from(this.productSelect.options).forEach((opt) => {
            const product = this.products[parseInt(opt.value, 10)];
            if (!product) return;
            const ann = this.chooserAnnotation(product, selections);
            opt.textContent = ann.text ? `${product.title} (${ann.text})` : product.title;
          });
        }
        if (this.chooserPills) {
          this.chooserPills.querySelectorAll('label').forEach((label) => {
            const input = document.getElementById(label.htmlFor);
            const product = input ? this.products[parseInt(input.value, 10)] : null;
            if (!product) return;
            const ann = this.chooserAnnotation(product, selections);
            let price = label.querySelector('.pseudo-chooser-pill__price');
            if (!price) {
              price = document.createElement('span');
              price.className = 'pseudo-chooser-pill__price';
              label.appendChild(price);
            }
            price.textContent = ann.text;
            price.hidden = !ann.text;
            label.classList.toggle('is-unavailable', ann.unavailable);
          });
        }
      }

      // The variant to preselect when a product is (re)built. Sticky choices
      // outrank availability (weight 2 vs 1), so the shopper's Count holds
      // across product switches even into a product that is sold out of it.
      preferredVariant(product) {
        const names = (product.options || []).map((n) => this.normName(n));
        let best = null;
        let bestScore = -1;
        product.variants.forEach((v) => {
          let score = v.available ? 1 : 0;
          names.forEach((name, i) => {
            if (this.chosenByName[name] != null && v.options[i] === this.chosenByName[name]) score += 2;
          });
          if (score > bestScore) {
            best = v;
            bestScore = score;
          }
        });
        return best || product.variants[0];
      }

      // ----- Option controls -----

      buildOptionSelects(product) {
        // Family-wide filter slots first: they exist independently of the
        // chooser block, so they render even without an options container.
        (this.optionSlots || []).forEach((slot) => this.buildFilterSlot(slot, product));

        if (!this.optionsContainer) return;
        this.optionsContainer.innerHTML = '';

        // Default each option to the sticky-aware preferred variant so the
        // initial combo is a real one and prior choices carry over.
        const preferred = this.preferredVariant(product);

        (product.options || []).forEach((optionName, optionIndex) => {
          // An option pinned to a filter slot renders there, not here.
          if (this.slotForOption(optionName)) return;
          let values = this.optionValues(product, optionIndex);
          // Optionally drop values that have no purchasable variant.
          if (this.hideSoldOut) {
            values = values.filter((value) => this.isValueAvailable(product, optionIndex, value));
          }
          // Nothing purchasable for this option: render no control for it.
          if (!values.length) return;
          // Skip an option that has only a single value with no real label
          // (e.g. the synthetic "Default Title" single-variant case).
          if (values.length === 1 && values[0] === 'Default Title') return;

          const field = this.buildOptionField(this.variantStyle, {
            optionName,
            label: optionName,
            values,
            selectedValue: preferred ? preferred.options[optionIndex] : null,
            group: `pseudo-option-${this.sectionId}-${optionIndex}`,
            idBase: `PseudoOpt-${this.sectionId}-${optionIndex}`,
            isFilter: false,
          });
          this.optionsContainer.appendChild(field);
        });
      }

      // One family-wide filter slot: union values with a sticky selection.
      buildFilterSlot(slot, product) {
        slot.innerHTML = '';
        const optionName = slot.dataset.optionName;
        const values = this.unionOptionValues(optionName, slot.dataset.hideSoldOut === 'true');
        if (!values.length) return;
        if (values.length === 1 && values[0] === 'Default Title') return;

        // A value nobody in the family can sell is a dead end: render it (so
        // the ladder stays complete) but DISABLE it, so it can never be
        // selected by click or keyboard.
        const disabledValues = values.filter((value) => !this.familyValueAvailable(optionName, value));

        const key = this.normName(optionName);
        let selectedValue = this.chosenByName[key];
        if (!values.includes(selectedValue) || disabledValues.includes(selectedValue)) selectedValue = null;
        if (selectedValue == null) {
          // First paint: start from the product's own preferred variant so
          // the page opens on a real, purchasable combination.
          const idx = this.optionIndexByName(product, optionName);
          const preferred = this.preferredVariant(product);
          if (
            idx >= 0 &&
            preferred &&
            values.includes(preferred.options[idx]) &&
            !disabledValues.includes(preferred.options[idx])
          ) {
            selectedValue = preferred.options[idx];
          }
        }
        if (selectedValue == null) {
          selectedValue = values.find((value) => !disabledValues.includes(value)) || values[0];
        }
        this.chosenByName[key] = selectedValue;

        const slotIndex = this.optionSlots.indexOf(slot);
        const field = this.buildOptionField(slot.dataset.optionStyle || 'card', {
          optionName,
          label: slot.dataset.optionLabel || optionName,
          values,
          selectedValue,
          disabledValues,
          group: `pseudo-filter-${this.sectionId}-${slotIndex}`,
          idBase: `PseudoFilter-${this.sectionId}-${slotIndex}`,
          isFilter: true,
        });
        slot.appendChild(field);
      }

      // Style dispatch + the shared field tagging gatherChosen relies on.
      buildOptionField(style, spec) {
        let field;
        if (style === 'pill') {
          field = this.buildPillField(spec);
        } else if (style === 'card') {
          field = this.buildCardField(spec);
        } else {
          field = this.buildSelectField(spec);
        }
        field.dataset.pseudoOptionField = '';
        field.dataset.optionName = spec.optionName;
        return field;
      }

      // Dropdown control for one option (default style).
      buildSelectField(spec) {
        const field = document.createElement('div');
        field.className = 'pseudo-field';

        const label = document.createElement('label');
        label.className = 'form__label';
        label.textContent = spec.label || spec.optionName;
        label.setAttribute('for', `${spec.idBase}-select`);
        field.appendChild(label);

        const wrap = document.createElement('div');
        wrap.className = 'select';

        const select = document.createElement('select');
        select.className = 'select__select';
        select.id = `${spec.idBase}-select`;

        spec.values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          if (spec.selectedValue === value) option.selected = true;
          if (spec.disabledValues && spec.disabledValues.includes(value)) option.disabled = true;
          select.appendChild(option);
        });

        select.addEventListener('change', () => this.resolveVariant());
        wrap.appendChild(select);
        if (this.caretTemplate) wrap.appendChild(this.caretTemplate.cloneNode(true));
        field.appendChild(wrap);
        return field;
      }

      // Pill (radio button) control for one option. Reuses Dawn's
      // product-form__input--pill markup + component-product-variant-picker.css.
      buildPillField(spec) {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'js product-form__input product-form__input--pill pseudo-field pseudo-field--pill';

        const legend = document.createElement('legend');
        legend.className = 'form__label';
        legend.textContent = spec.label || spec.optionName;
        fieldset.appendChild(legend);

        spec.values.forEach((value, valueIndex) => {
          const id = `${spec.idBase}-${valueIndex}`;

          const input = document.createElement('input');
          input.type = 'radio';
          input.id = id;
          input.name = spec.group;
          input.value = value;
          if (spec.selectedValue === value) input.checked = true;
          if (spec.disabledValues && spec.disabledValues.includes(value)) input.disabled = true;
          input.addEventListener('change', () => this.resolveVariant());

          const label = document.createElement('label');
          label.setAttribute('for', id);
          label.textContent = value;

          fieldset.appendChild(input);
          fieldset.appendChild(label);
        });

        return fieldset;
      }

      // Card control for one option: a tile per value showing the value, the
      // price of the variant it resolves to, and (when the value states a
      // count, e.g. "1 x 96 CT") the derived per-unit price. Radio-driven, so it
      // shares the pill path everywhere else. Prices are written by
      // refreshCardPrices, which resolveVariant runs right after every build.
      buildCardField(spec) {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'js product-form__input pseudo-field pseudo-field--card';

        const legend = document.createElement('legend');
        legend.className = 'form__label';
        legend.textContent = spec.label || spec.optionName;
        fieldset.appendChild(legend);

        const grid = document.createElement('div');
        grid.className = 'pseudo-cards';

        spec.values.forEach((value, valueIndex) => {
          const id = `${spec.idBase}-${valueIndex}`;

          const input = document.createElement('input');
          input.type = 'radio';
          input.id = id;
          input.name = spec.group;
          input.value = value;
          if (spec.selectedValue === value) input.checked = true;
          if (spec.disabledValues && spec.disabledValues.includes(value)) input.disabled = true;
          input.addEventListener('change', () => this.resolveVariant());

          const label = document.createElement('label');
          label.className = 'pseudo-card';
          label.setAttribute('for', id);
          label.dataset.optionName = spec.optionName;
          label.dataset.value = value;
          if (spec.isFilter) label.dataset.filterCard = 'true';

          const head = document.createElement('span');
          head.className = 'pseudo-card__head';
          head.textContent = this.cardHeadLabel(spec.optionName, value);

          const body = document.createElement('span');
          body.className = 'pseudo-card__body';

          const price = document.createElement('span');
          price.className = 'pseudo-card__price';
          const unit = document.createElement('span');
          unit.className = 'pseudo-card__unit';

          body.appendChild(price);
          body.appendChild(unit);
          label.appendChild(head);
          label.appendChild(body);

          grid.appendChild(input);
          grid.appendChild(label);
        });

        fieldset.appendChild(grid);
        return fieldset;
      }

      // The variant an option value resolves to, holding the other options at
      // `context` (the preferred variant at build time, the current variant on
      // refresh). Exact for the usual single-option pseudo product.
      variantForValue(product, optionIndex, value, context) {
        if (!product) return null;
        const inCombo = (v) =>
          v.options[optionIndex] === value &&
          (!context || v.options.every((o, i) => i === optionIndex || o === context.options[i]));
        const hasValue = (v) => v.options[optionIndex] === value;
        return (
          product.variants.find((v) => inCombo(v) && v.available) ||
          product.variants.find(inCombo) ||
          product.variants.find((v) => hasValue(v) && v.available) ||
          product.variants.find(hasValue) ||
          null
        );
      }

      // The terse "N x M CT" form only. Null when the value does not state a
      // count that way, which keeps non-count options (e.g. "750 mL", "Dark")
      // rendering their raw label.
      parseCountValue(value) {
        const match = String(value).match(/^\s*(\d+)\s*[x×]\s*(\d+)\s*([a-z.]*)\s*$/i);
        if (!match) return null;
        const packs = parseInt(match[1], 10);
        const per = parseInt(match[2], 10);
        if (!packs || !per) return null;
        const unit = (match[3] || '').replace(/\./g, '');
        if (unit && !/^(ct|cts|count|counts|pod|pods|pk|pack|packs|ea|each)$/i.test(unit)) return null;
        return { total: packs * per, packs };
      }

      // Total units an option value states, used for the per-unit price. Reads
      // both the terse "1 x 96 CT" form and values that lead with the total,
      // e.g. "48 PODS (4 x 12 CT BAGS)". Null when the value states no count.
      totalCountForValue(value) {
        const compact = this.parseCountValue(value);
        if (compact) return compact.total;
        const leading = String(value).match(/^\s*(\d+)\s*(pods?|ct|cts|count|counts)\b/i);
        if (!leading) return null;
        const total = parseInt(leading[1], 10);
        return total > 0 ? total : null;
      }

      // Card heading: "96 Count (Pack of 1)" for the terse "1 x 96 CT" form,
      // which is not shopper-facing language. The noun comes from the option
      // name, not a hard-coded word. Any other value keeps the merchant's own
      // wording verbatim (e.g. "48 PODS (4 x 12 CT BAGS)"); it still gets a
      // per-unit line when a total can be read out of it.
      cardHeadLabel(optionName, value) {
        const count = this.parseCountValue(value);
        if (!count) return value;
        return `${count.total} ${optionName} (Pack of ${count.packs})`;
      }

      // Fill one card's price, per-unit line and availability classes.
      // `variant` is the current product's variant for the card's value. A
      // FILTER card stays family-honest: when the current product skips the
      // value, or is sold out of it while a sibling has stock, the card shows
      // the family's cheapest price at that value prefixed "from". The tile
      // only dims when nobody in the family can sell it.
      writeCardState(card, optionName, value, variant, isFilter) {
        const priceEl = card.querySelector('.pseudo-card__price');
        const unitEl = card.querySelector('.pseudo-card__unit');
        if (!priceEl || !unitEl) return;

        let shown = variant;
        let from = false;
        if (isFilter && (!shown || !shown.available)) {
          const cheap = this.cheapestFamilyVariant(optionName, value);
          if (cheap && (cheap.available || !shown)) {
            from = true;
            shown = cheap;
          }
        }

        priceEl.textContent = shown ? (from ? `from ${shown.price}` : shown.price) : '';

        const total = this.totalCountForValue(value);
        if (shown && total && total > 1 && shown.price_amount != null) {
          const each = this.formatMoney(shown.price_amount / total);
          unitEl.textContent = `(${each} / ${optionName.toLowerCase()})`;
          unitEl.hidden = false;
        } else {
          unitEl.textContent = '';
          unitEl.hidden = true;
        }

        card.classList.toggle('pseudo-card--from', from);
        card.classList.toggle('is-unavailable', !shown || !shown.available);
      }

      // Recompute every card (chooser-block fields AND filter slots) against
      // the current product and resolved variant. Runs on every resolve:
      // multi-option products need it when a sibling option changes, and the
      // filter cards need it on every product switch.
      refreshCardPrices() {
        const product = this.currentProduct;
        if (!product) return;
        this.querySelectorAll('.pseudo-card').forEach((card) => {
          const optionName = card.dataset.optionName;
          const value = card.dataset.value;
          const isFilter = card.dataset.filterCard === 'true';
          const optionIndex = this.optionIndexByName(product, optionName);
          const variant =
            optionIndex >= 0 ? this.variantForValue(product, optionIndex, value, this.currentVariant) : null;
          this.writeCardState(card, optionName, value, variant, isFilter);
        });
      }

      // The chosen value per option NAME (normalized), from every option field
      // on the page: the chooser block's fields and the filter slots, whatever
      // style each renders as.
      gatherChosen() {
        const chosen = {};
        this.querySelectorAll('[data-pseudo-option-field]').forEach((field) => {
          const name = this.normName(field.dataset.optionName);
          const select = field.querySelector('select');
          if (select) {
            chosen[name] = select.value;
            return;
          }
          const checked = field.querySelector('input[type="radio"]:checked');
          if (checked) chosen[name] = checked.value;
        });
        return chosen;
      }

      resolveVariant() {
        const product = this.currentProduct;
        if (!product) return;

        const chosen = this.gatherChosen();
        Object.assign(this.chosenByName, chosen);
        const names = Object.keys(chosen);
        let variant;

        if (names.length) {
          // Every chosen name must match. A product that lacks a filtered
          // option name entirely matches nothing: it has no such Count, so it
          // is honestly unavailable under the filter.
          variant = product.variants.find((v) =>
            names.every((name) => {
              const idx = this.optionIndexByName(product, name);
              return idx >= 0 && v.options[idx] === chosen[name];
            })
          );
        } else {
          // Single default variant products have no option controls.
          variant = product.variants.find((v) => v.available) || product.variants[0];
        }

        this.currentVariant = variant || null;
        this.refreshCardPrices();
        this.updatePrice(variant);
        this.updateChooserLabel(variant);
        this.updateVariantImage(variant);
        this.updateBuyState(variant);
        this.updateSubscription(variant);
        // LAST on purpose: a changed filter may remove the current product
        // from the chooser and auto-advance to another one, and that product's
        // own resolve must overwrite everything written above.
        this.updateChooserForResolve();
      }

      // Reflect the resolved variant's price in the product chooser <select>, so
      // the closed dropdown shows the price the pills currently resolve to.
      // variant.price is already a money-formatted string from the JSON payload,
      // matching the server-rendered default label. Only the selected option is
      // updated; the others keep their server-rendered default price. With a
      // family filter active, refreshChooserAnnotations owns every label.
      updateChooserLabel(variant) {
        if (!this.productSelect || this.hasFilters) return;
        // Options may be a filtered subset, so match by value, not position.
        const opt = Array.from(this.productSelect.options).find((o) => o.value === String(this.currentIndex));
        const product = this.currentProduct;
        if (!opt || !product) return;
        opt.textContent = variant ? `${product.title} (${variant.price})` : product.title;
      }

      updatePrice(variant) {
        if (!this.priceEl) return;
        if (!variant) {
          // Under a family filter, "no variant" means the current product is
          // not offered at the filtered value: say so instead of going blank.
          this.priceEl.textContent = this.hasFilters ? this.strings.unavailable || 'Unavailable' : '';
          if (this.compareEl) this.compareEl.hidden = true;
          return;
        }

        // In sync mode show unit price x quantity; otherwise the preformatted
        // unit price string straight from the payload.
        const qty = this.priceSyncQuantity ? this.currentQuantity() : 1;
        const multiply = this.priceSyncQuantity && variant.price_amount != null;

        this.priceEl.textContent = multiply
          ? this.formatMoney(variant.price_amount * qty)
          : variant.price;

        if (this.compareEl) {
          if (variant.compare_at) {
            this.compareEl.textContent =
              multiply && variant.compare_at_amount != null
                ? this.formatMoney(variant.compare_at_amount * qty)
                : variant.compare_at;
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }
      }

      currentQuantity() {
        const q = parseInt(this.quantityInput?.value, 10);
        return q && q > 0 ? q : 1;
      }

      // Compact Shopify-style money formatter (the theme ships no formatMoney
      // util). cents is an integer; moneyFormat comes from shop.money_format.
      formatMoney(cents) {
        const format = this.moneyFormat || '${{amount}}';
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const placeholder = match ? match[1] : 'amount';
        const build = (precision, thousands, decimal) => {
          const n = (cents / 100).toFixed(precision);
          const parts = n.split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          return parts[1] ? whole + decimal + parts[1] : whole;
        };
        let value;
        switch (placeholder) {
          case 'amount_no_decimals':
            value = build(0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = build(2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = build(0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = build(2, "'", '.');
            break;
          case 'amount_with_space_separator':
            value = build(2, ' ', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = build(0, ' ', '');
            break;
          default:
            value = build(2, ',', '.');
        }
        return match ? format.replace(match[0], value) : format + value;
      }

      buildGallery(product) {
        if (!this.thumbsEl) return;
        this.thumbsEl.innerHTML = '';

        const images = product.images && product.images.length ? product.images : (product.featured_image ? [product.featured_image] : []);
        const thumbs = product.thumbs && product.thumbs.length ? product.thumbs : images;

        this.setMainImage(images[0] || product.featured_image || '', product.title);

        if (images.length < 2) return; // no strip needed for a single image

        images.forEach((full, i) => {
          const li = document.createElement('li');
          li.className = 'pseudo-gallery__thumb';

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pseudo-gallery__thumb-button';
          button.setAttribute('aria-label', `${product.title} image ${i + 1}`);

          const img = document.createElement('img');
          img.src = thumbs[i] || full;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 100;
          img.height = 100;

          button.dataset.full = full;
          button.appendChild(img);
          button.addEventListener('click', () => this.setMainImage(full, product.title));
          li.appendChild(button);
          this.thumbsEl.appendChild(li);
        });
      }

      setMainImage(url, alt) {
        if (!this.mainImage || !url) return;
        this.mainImage.src = url;
        this.mainImage.removeAttribute('srcset');
        if (alt) this.mainImage.alt = alt;

        // Reflect the active thumb. Thumb <img> uses a smaller width URL, so
        // compare against the full-size URL stored on the button instead.
        if (this.thumbsEl) {
          this.thumbsEl.querySelectorAll('.pseudo-gallery__thumb-button').forEach((b) => {
            b.classList.toggle('is-active', b.dataset.full === url);
          });
        }
      }

      updateVariantImage(variant) {
        if (variant && variant.image) {
          this.setMainImage(variant.image, this.currentProduct?.title);
        }
      }

      updateBuyState(variant) {
        if (!this.submitButton) return;
        const available = !!(variant && variant.available);

        if (variant && this.variantIdInput) this.variantIdInput.value = variant.id;

        if (available) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = this.strings.addToCart || 'Add to cart';
        } else {
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = variant
              ? this.strings.soldOut || 'Sold out'
              : this.strings.unavailable || 'Unavailable';
          }
        }
      }

      // ----- Subscription (native Shopify selling plans) -----

      // Rebuild the purchase-options UI for the resolved variant. Hides the whole
      // block (and forces a one-time add) when the variant carries no plans.
      updateSubscription(variant) {
        if (!this.subscriptionWrapper) return;

        const plans = (variant && Array.isArray(variant.selling_plans) && variant.selling_plans) || [];

        if (!plans.length) {
          this.subscriptionWrapper.hidden = true;
          // Reset to one-time so a subsequent add is not accidentally a subscription.
          const onetime = this.querySelector('[data-pseudo-purchase-option="onetime"]');
          if (onetime) onetime.checked = true;
          this.subscriptionWrapper.dataset.selected = 'onetime';
          if (this.frequencyWrap) this.frequencyWrap.hidden = true;
          this.syncSellingPlanInput();
          return;
        }

        this.subscriptionWrapper.hidden = false;

        // One-time price mirrors the variant's unit price (never quantity-multiplied,
        // matching how subscription widgets present the per-unit choice).
        if (this.onetimePriceEl && variant) this.onetimePriceEl.textContent = variant.price;

        this.buildFrequencyOptions(plans);
        this.updateSubscribePrice();
        this.syncSellingPlanInput();
      }

      // Populate the frequency <select> from the variant's plans, preserving the
      // shopper's cadence choice by plan name where possible.
      buildFrequencyOptions(plans) {
        if (!this.frequencySelect) return;
        this.frequencySelect.innerHTML = '';
        plans.forEach((plan) => {
          const option = document.createElement('option');
          option.value = String(plan.id);
          option.textContent = plan.name;
          option.dataset.planName = plan.name || '';
          this.frequencySelect.appendChild(option);
        });

        // Restore prior cadence by name, else default to the first plan.
        const match = this.selectedFrequencyName
          ? plans.find((p) => p.name === this.selectedFrequencyName)
          : null;
        const chosen = match || plans[0];
        this.frequencySelect.value = String(chosen.id);
        this.selectedFrequencyName = chosen.name;
      }

      // The plan object currently selected in the frequency <select>.
      selectedPlan() {
        const plans = (this.currentVariant && this.currentVariant.selling_plans) || [];
        if (!plans.length) return null;
        const id = this.frequencySelect ? this.frequencySelect.value : '';
        return plans.find((p) => String(p.id) === String(id)) || plans[0];
      }

      // Render the subscribe option's price, strikethrough and savings badge.
      updateSubscribePrice() {
        const plan = this.selectedPlan();
        if (!plan) return;

        if (this.subPriceEl) this.subPriceEl.textContent = plan.price;

        if (this.subCompareEl) {
          if (plan.compare_at) {
            this.subCompareEl.textContent = plan.compare_at;
            this.subCompareEl.hidden = false;
          } else {
            this.subCompareEl.hidden = true;
          }
        }

        if (this.subBadgeEl) {
          const pct =
            plan.compare_at_amount && plan.price_amount != null && plan.compare_at_amount > 0
              ? Math.round(((plan.compare_at_amount - plan.price_amount) / plan.compare_at_amount) * 100)
              : 0;
          if (pct > 0) {
            this.subBadgeEl.textContent = `Save ${pct}%`;
            this.subBadgeEl.hidden = false;
          } else {
            this.subBadgeEl.hidden = true;
          }
        }
      }

      currentPurchaseOption() {
        const checked = this.querySelector('[data-pseudo-purchase-option]:checked');
        return checked ? checked.value : 'onetime';
      }

      onPurchaseOptionChange() {
        const option = this.currentPurchaseOption();
        this.subscriptionWrapper.dataset.selected = option;
        if (this.frequencyWrap) this.frequencyWrap.hidden = option !== 'subscribe';
        this.syncSellingPlanInput();
      }

      onFrequencyChange() {
        this.selectedFrequencyName =
          this.selectedPlan()?.name || this.selectedFrequencyName;
        this.updateSubscribePrice();
        this.syncSellingPlanInput();
      }

      // Reflect the current purchase choice into the hidden [name=selling_plan].
      // Disabled (so /cart/add omits it) unless subscribing to a real plan.
      syncSellingPlanInput() {
        if (!this.sellingPlanInput) return;
        const subscribing = this.currentPurchaseOption() === 'subscribe';
        const plan = subscribing ? this.selectedPlan() : null;
        if (plan) {
          this.sellingPlanInput.value = String(plan.id);
          this.sellingPlanInput.disabled = false;
        } else {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
        }
      }
    }
  );
}
